import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X, Volume2, VolumeX, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface HomepageVideo {
  id: string;
  title: string;
  video_url: string | null;
  thumbnail_url: string | null;
  display_order: number;
}

interface FullscreenModalProps {
  videos: HomepageVideo[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const FullscreenVideoModal = ({ videos, currentIndex, onClose, onNavigate }: FullscreenModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [hasPlaybackError, setHasPlaybackError] = useState(false);

  const video = videos[currentIndex];
  const hasNext = currentIndex < videos.length - 1;
  const hasPrev = currentIndex > 0;

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const goToNext = useCallback(() => {
    if (hasNext) {
      onNavigate(currentIndex + 1);
    }
  }, [hasNext, currentIndex, onNavigate]);

  const goToPrev = useCallback(() => {
    if (hasPrev) {
      onNavigate(currentIndex - 1);
    }
  }, [hasPrev, currentIndex, onNavigate]);

  useEffect(() => {
    // Reset error state and auto-play with sound when video changes
    setHasPlaybackError(false);
    if (videoRef.current && video.video_url) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {
        // autoplay can be blocked on mobile; keep UI usable
      });
      setIsPlaying(true);
      setProgress(0);
      setCurrentTime(0);
    }
  }, [currentIndex, video.video_url]);

  // Handle video time updates for progress bar
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      if (videoElement.duration) {
        setCurrentTime(videoElement.currentTime);
        setProgress((videoElement.currentTime / videoElement.duration) * 100);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [currentIndex]);

  useEffect(() => {
    // Handle escape key and arrow keys
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, goToNext, goToPrev]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!progressRef.current || !videoRef.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    const newTime = clickPosition * duration;
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(clickPosition * 100);
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-50 w-12 h-12 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors"
        aria-label="Close video"
      >
        <X className="h-6 w-6 text-foreground" />
      </button>

      {/* Navigation arrows - Desktop */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); goToPrev(); }}
          className="absolute left-4 md:left-8 z-50 w-12 h-12 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors hidden md:flex"
          aria-label="Previous video"
        >
          <ChevronLeft className="h-6 w-6 text-foreground" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); goToNext(); }}
          className="absolute right-4 md:right-8 z-50 w-12 h-12 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors hidden md:flex"
          aria-label="Next video"
        >
          <ChevronRight className="h-6 w-6 text-foreground" />
        </button>
      )}

      {/* Video counter */}
      <div className="absolute top-6 left-6 z-50 px-3 py-1.5 rounded-full bg-muted/80 text-sm text-foreground">
        {currentIndex + 1} / {videos.length}
      </div>

      {/* Video container with swipe support */}
      <motion.div 
        key={currentIndex}
        className="relative w-full max-w-md mx-4 aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -50 }}
        transition={{ duration: 0.2 }}
      >
        {/* Always render a visible background so mobile never looks "blank" */}
        {video.thumbnail_url ? (
          <img
            src={video.thumbnail_url}
            alt={video.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--primary) / 0.15) 50%, hsl(var(--muted)) 100%)`,
            }}
          />
        )}

        {!hasPlaybackError && (
          <video
            ref={videoRef}
            src={video.video_url || ''}
            className="absolute inset-0 w-full h-full object-cover"
            loop
            playsInline
            autoPlay
            poster={video.thumbnail_url || undefined}
            onError={() => setHasPlaybackError(true)}
          />
        )}

        {/* Swipe hint for mobile */}
        <div className="absolute top-1/2 left-0 right-0 flex justify-between px-2 pointer-events-none md:hidden">
          {hasPrev && (
            <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center">
              <ChevronLeft className="h-4 w-4 text-foreground/70" />
            </div>
          )}
          <div className="flex-1" />
          {hasNext && (
            <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center">
              <ChevronRight className="h-4 w-4 text-foreground/70" />
            </div>
          )}
        </div>

        {/* Controls overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background/90 via-background/50 to-transparent">
          <p className="text-lg font-medium text-foreground mb-3">{video.title}</p>
          
          {/* Progress bar */}
          <div className="mb-4">
            <div 
              ref={progressRef}
              className="relative h-1.5 bg-muted/50 rounded-full cursor-pointer group"
              onClick={handleProgressClick}
            >
              <div 
                className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
              {/* Scrubber handle */}
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                style={{ left: `calc(${progress}% - 6px)` }}
              />
            </div>
            <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className="w-12 h-12 rounded-full bg-primary/90 hover:bg-primary flex items-center justify-center transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 text-primary-foreground" />
              ) : (
                <Play className="h-5 w-5 text-primary-foreground ml-0.5" />
              )}
            </button>

            {/* Mute/Unmute */}
            <button
              onClick={toggleMute}
              className="w-12 h-12 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <VolumeX className="h-5 w-5 text-foreground" />
              ) : (
                <Volume2 className="h-5 w-5 text-foreground" />
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
const VideoCard = ({ 
  video, 
  index, 
  onOpenFullscreen 
}: { 
  video: HomepageVideo; 
  index: number;
  onOpenFullscreen: (video: HomepageVideo) => void;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasPreviewError, setHasPreviewError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Use IntersectionObserver for mobile auto-play on scroll
  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting);
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Auto-play video when in view on mobile (no hover events)
  useEffect(() => {
    if (!videoRef.current || !video.video_url) return;
    
    // Check if mobile (no hover capability)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (isTouchDevice && isInView) {
      videoRef.current.play().catch(() => {
        // Autoplay might be blocked, that's okay
      });
      setIsPlaying(true);
    } else if (isTouchDevice && !isInView && isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isInView, video.video_url, isPlaying]);

  const handleClick = () => {
    if (video.video_url) {
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
      onOpenFullscreen(video);
    }
  };

  const handleMouseEnter = () => {
    // Only trigger on non-touch devices
    if ('ontouchstart' in window) return;
    if (videoRef.current && video.video_url && !isPlaying) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    // Only trigger on non-touch devices
    if ('ontouchstart' in window) return;
    if (videoRef.current && isPlaying) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  return (
    <motion.div
      ref={cardRef}
      className="relative aspect-[9/16] bg-muted rounded-lg overflow-hidden group cursor-pointer"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Always render a visible background so mobile never looks "blank" */}
      {video.thumbnail_url ? (
        <img 
          src={video.thumbnail_url} 
          alt={video.title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div 
          className="absolute inset-0 bg-gradient-to-br from-muted via-muted-foreground/10 to-muted"
          style={{
            backgroundImage: `linear-gradient(135deg, 
              hsl(var(--muted)) 0%, 
              hsl(var(--primary) / 0.15) 50%, 
              hsl(var(--muted)) 100%)`
          }}
        />
      )}

      {/* Video element (over the background) */}
      {video.video_url && !hasPreviewError && (
        <video
          ref={videoRef}
          src={video.video_url}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted
          playsInline
          preload="metadata"
          poster={video.thumbnail_url || undefined}
          onError={() => setHasPreviewError(true)}
        />
      )}

      {/* Title overlay at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background/90 to-transparent">
        <p className="text-sm font-medium text-foreground">{video.title}</p>
      </div>

      {/* Play button overlay */}
      {video.video_url && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className={`
              w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center
              transition-all duration-300
              ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}
            `}
          >
            <Play className="h-6 w-6 text-primary-foreground ml-1" />
          </div>
        </div>
      )}

      {/* Subtle border */}
      <div className="absolute inset-0 rounded-lg ring-1 ring-foreground/10" />
    </motion.div>
  );
};

export const VerticalVideosSection = () => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['homepage-videos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('homepage_videos')
        .select('id, title, video_url, thumbnail_url, display_order')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data as HomepageVideo[];
    },
  });

  const handleOpenFullscreen = (video: HomepageVideo) => {
    const index = videos.findIndex(v => v.id === video.id);
    setFullscreenIndex(index >= 0 ? index : null);
  };

  // Don't render section if no videos
  if (!isLoading && videos.length === 0) {
    return null;
  }

  return (
    <>
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-primary font-medium tracking-wide uppercase text-sm mb-4 block">
              Featured Reels
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              Cinematic Moments
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Experience our signature storytelling through these captivating vertical films
            </p>
          </motion.div>

          {/* Video Grid */}
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="relative aspect-[9/16] bg-muted rounded-lg overflow-hidden">
                  {/* Shimmer background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-muted via-muted-foreground/5 to-muted animate-pulse" />
                  {/* Title skeleton */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="h-4 w-3/4 bg-muted-foreground/10 rounded animate-pulse" />
                  </div>
                  {/* Play button skeleton */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-muted-foreground/10 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {videos.map((video, index) => (
                <VideoCard 
                  key={video.id} 
                  video={video} 
                  index={index} 
                  onOpenFullscreen={handleOpenFullscreen}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Fullscreen Video Modal */}
      <AnimatePresence>
        {fullscreenIndex !== null && videos.length > 0 && (
          <FullscreenVideoModal
            videos={videos}
            currentIndex={fullscreenIndex}
            onClose={() => setFullscreenIndex(null)}
            onNavigate={setFullscreenIndex}
          />
        )}
      </AnimatePresence>
    </>
  );
};
